/**
 * Canonical secret redaction module (ADRs D68-D73).
 *
 * Single source of truth for credential pattern masking across the SDK.
 * Wired at output boundaries: `ErrorMetadata.raw` (mappers/shared.ts),
 * telemetry span attributes (telemetry/tracer.ts), transcript JSONL
 * appends (agent-session-store.ts), migration logger output
 * (memory/migrate-sqlite-to-lance.ts).
 *
 * - D68: central module, single source of truth (replaces 2 duplicates)
 * - D69: env snapshot at module init (prompt-injection defense)
 * - D70: ON by default, warn on opt-out
 * - D71: two-bucket masking — short fully masked, long preserves prefix+suffix
 * - D72: `codeFile` opt-out for legitimate prefix-shaped content
 * - D73: redact at OUTPUT boundaries, not at storage
 *
 * @internal
 */

// D69: env snapshot captured at module load. Subsequent mutations of
// process.env.THEOKIT_REDACT_SECRETS are ignored — defends against
// prompt injection that tries to disable redaction mid-run.
let REDACT_ENABLED: boolean = readEnvOnce();

function readEnvOnce(): boolean {
  const raw = process.env.THEOKIT_REDACT_SECRETS;
  if (raw === undefined) return true; // D70: default ON
  return ["1", "true", "yes", "on"].includes(raw.toLowerCase());
}

// D70: warn once on opt-out so the user knows they're vulnerable.
let warnedOptOut = false;
if (!REDACT_ENABLED && !warnedOptOut) {
  process.stderr.write(
    "[theokit-sdk] Secret redaction is DISABLED via THEOKIT_REDACT_SECRETS. " +
      "Credentials may leak into errors, telemetry, logs, transcripts.\n",
  );
  warnedOptOut = true;
}

/**
 * Built-in credential patterns. Order matters — more specific prefixes
 * must come before generic ones (e.g., `sk-ant-` before `sk-`). Quantifiers
 * are all bounded `{n,m}` or applied to char classes — linear time, no ReDoS.
 *
 * @internal
 */
const BUILTIN_PATTERNS: readonly RegExp[] = [
  // T5.4: 30+ vendor prefixes (was 12 pre-T5.4). Order matters — more
  // specific prefixes precede generic ones (e.g., sk-ant-admin01 before
  // sk-ant-, sk-proj- before sk-). PEM block deliberately first so its
  // multi-line span runs before any per-line patterns can fire.
  /-----BEGIN[ ]+(?:RSA |EC |DSA |OPENSSH |ENCRYPTED |)PRIVATE KEY-----[\s\S]+?-----END[ ]+(?:RSA |EC |DSA |OPENSSH |ENCRYPTED |)PRIVATE KEY-----/g,
  // JWT — exact 3-segment base64url. Dotted; the body floor of 4 chars per
  // segment matches the minimum legal payload while skipping `a.b.c` noise.
  /eyJ[A-Za-z0-9_-]{4,}\.eyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}/g,
  // Azure Storage SAS — match the sig= component (URL-encoded base64).
  /(?<=[?&]sig=)[A-Za-z0-9%+/]{20,}/g,
  // Anthropic
  /sk-ant-admin01-[A-Za-z0-9_-]{10,}/g, //   Anthropic admin keys (must precede sk-ant-)
  /sk-ant-[A-Za-z0-9_-]{10,}/g, //           Anthropic regular
  // OpenAI family + clones (sk- generic must come AFTER all sk-foo- variants)
  /sk-proj-[A-Za-z0-9_-]{10,}/g, //  OpenAI project key (must precede sk- generic)
  /sk-[A-Za-z0-9_-]{10,}/g, //       OpenAI / OpenRouter / DeepInfra / Together / DeepSeek
  // Provider prefixes (alphabetized for maintainability)
  /AIza[A-Za-z0-9_-]{35}/g, //       Google API key
  /AKIA[A-Z0-9]{16}/g, //            AWS access key
  /fw_[A-Za-z0-9]{20,}/g, //         Fireworks
  /glpat-[A-Za-z0-9_-]{20}/g, //     GitLab PAT
  /ghp_[A-Za-z0-9]{36}/g, //         GitHub PAT classic
  /github_pat_[A-Za-z0-9_]{82}/g, // GitHub PAT fine-grained
  /gsk_[A-Za-z0-9]{20,}/g, //        Groq
  /hf_[A-Za-z0-9]{20,}/g, //         HuggingFace
  /\bpa-[A-Za-z0-9_-]{20,}/g, //     Voyage AI (word-boundary to skip CSS / kebab IDs)
  /pcsk_[A-Za-z0-9_-]{20,}/g, //     Pinecone
  /pplx-[A-Za-z0-9_-]{20,}/g, //     Perplexity
  /r8_[A-Za-z0-9_-]{20,}/g, //       Replicate
  /rk_live_[A-Za-z0-9]{20,}/g, //    Stripe restricted
  /sk_live_[A-Za-z0-9]{20,}/g, //    Stripe secret
  /sntrys_[A-Za-z0-9]{40,}/g, //     Sentry user auth
  /xai-[A-Za-z0-9_-]{20,}/g, //      xAI (Grok)
  /xox[bpasr]-[A-Za-z0-9-]{10,}/g, //Slack tokens
  // Additional unique-prefix tokens with low false-positive risk
  /npm_[A-Za-z0-9]{36}/g, //         npm access token
  /SG\.[A-Za-z0-9_-]{22}\.[A-Za-z0-9_-]{43}/g, // SendGrid
  /\bSK[A-Za-z0-9]{32}\b/g, //       Twilio API SID (word-boundary to skip CSS class noise)
  /\bkey-[a-f0-9]{32}\b/g, //        Mailgun (hex-only narrows false positives)
  /MT[A-Za-z0-9_-]{23}\.[A-Za-z0-9_-]{6}\.[A-Za-z0-9_-]{27}/g, // Discord bot
  /\b(?:sdk|mob)-[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/g, // LaunchDarkly
];

// `Bearer <token>` matched as its own first-class pattern so PARAM_PATTERN
// doesn't have to handle the unusual `Authorization: Bearer xxx` shape
// (no `:` or `=` between "Bearer" and the value — bare whitespace).
const BEARER_PATTERN = /\b(Bearer\s+)([A-Za-z0-9_\-.+/=]{8,})/g;

// Parametric: matches `key=value` and `key: value` (with optional quote
// between the key and the separator, to handle JSON: `"api_key": "..."`)
// in URLs, query strings, JSON-like bodies, HTTP headers. Captures the
// prefix so we keep it visible while masking the value.
//
// `authorization` deliberately excluded — BEARER_PATTERN handles the
// common `Authorization: Bearer xxx` shape. Including it here causes
// double-masking ("Authorization: *** ***") after Bearer fires.
// T5.4: keyword set expanded from 6 → 16 to cover the OAuth / JWT / generic
// credential vocabulary surfaced by DR6 finding #4. `authorization`,
// `auth`, `bearer` stay excluded — BEARER_PATTERN handles the
// `Authorization: Bearer xxx` shape and including these here would
// re-catch the post-BUILTIN-masked form (D71 prefix-preservation
// contract) and double-mask to `***`.
//
// Value class includes `.` so JWT / `.env` / dotted base64url values
// match; the callback skips already-masked values (containing the
// `...` D71 separator) to preserve the BUILTIN prefix-mask result.
const PARAM_PATTERN =
  /(\b(?:access_token|api_key|api-key|client_secret|credential|credentials|id_token|jwt|password|private_key|refresh_token|secret|service_account|session_token|token|x-api-key)\b["']?\s*[:=]\s*["']?)([A-Za-z0-9_\-.+/]+)/gi;

const _extraPatterns: RegExp[] = [];

/**
 * Add a user-defined redaction pattern. Additive — never removes builtins.
 * Throws if the regex lacks the `/g` flag (without `/g`, `.replace` only
 * substitutes the first match and the rest leaks).
 *
 * @internal — exposed publicly via `Security.addPattern` in `src/security.ts`.
 */
export function addPattern(re: RegExp): void {
  if (!re.global) {
    throw new Error("Security.addPattern: regex must have /g flag for replace-all semantics");
  }
  _extraPatterns.push(re);
}

/**
 * Two-bucket masking (D71):
 *   - tokens shorter than 18 chars → fully masked as `***`
 *   - tokens >= 18 chars → keep first 6 + `...` + last 4
 *
 * Rationale: long tokens are unique per-account; prefix+suffix preserves
 * debuggability without revealing the secret middle.
 *
 * @internal
 */
export function maskToken(token: string): string {
  if (token.length < 18) return "***";
  return `${token.slice(0, 6)}...${token.slice(-4)}`;
}

/**
 * Redact known credential patterns from `text`. Default behavior masks
 * builtins + extras + parametric `key=value` sinks.
 *
 * With `{ codeFile: true }` (D72), skips PARAM_PATTERN to avoid mangling
 * `.env.example`, schema JSON, or test fixtures that legitimately contain
 * prefix-like strings.
 *
 * Returns the redacted string. Coerces non-strings via JSON.stringify;
 * EC-7 fix (edge-case review): wraps in try/catch so circular references
 * never propagate — returns sentinel `"[unredactable: circular]"`.
 *
 * @internal
 */
// Coerce arbitrary input to a string for redaction. Returns `null`
// sentinel when the value is null/undefined/non-stringifiable, so the
// caller can short-circuit with `""`. EC-7 fix: circular refs go through
// the try/catch and produce the sentinel marker, never throwing.
function coerceToString(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return null;
  if (typeof value === "object") {
    try {
      const s = JSON.stringify(value);
      return s === undefined ? null : s;
    } catch {
      return "[unredactable: circular]";
    }
  }
  return String(value);
}

export function redactSecrets(text: unknown, opts?: { codeFile?: boolean }): string {
  const coerced = coerceToString(text);
  if (coerced === null) return "";
  if (!REDACT_ENABLED) return coerced;

  let s = coerced;
  for (const re of BUILTIN_PATTERNS) {
    s = s.replace(re, (m) => maskToken(m));
  }
  for (const re of _extraPatterns) {
    s = s.replace(re, (m) => maskToken(m));
  }
  if (!opts?.codeFile) {
    // Bearer first (preserves "Bearer " prefix, masks the token after).
    // Must run before PARAM_PATTERN so the bare-whitespace shape doesn't
    // get mis-handled as a value.
    s = s.replace(BEARER_PATTERN, (_, prefix: string) => `${prefix}***`);
    // T5.4: skip if value already contains the D71 bucket-mask separator
    // (`...`) — BUILTIN ran first and produced a prefix-preserved mask;
    // re-masking would lose the prefix and degrade debuggability.
    s = s.replace(PARAM_PATTERN, (whole, prefix: string, value: string) => {
      if (value.includes("...")) return whole;
      return `${prefix}***`;
    });
  }
  return s;
}

/**
 * Test-only helper exported for `_test-reset.ts`. NOT included in the
 * `index.ts` barrel — vitest setup imports the dedicated module via
 * explicit path to discourage production callers.
 *
 * @internal
 */
export function _resetForTests(opts: { enabled?: boolean; clearExtras?: boolean }): void {
  if (opts.enabled !== undefined) REDACT_ENABLED = opts.enabled;
  if (opts.clearExtras === true) _extraPatterns.length = 0;
}

/**
 * T5.4 — Test-only count of BUILTIN_PATTERNS. Exposed so the count-floor
 * assertion can run without re-deriving the array shape in test land.
 * NOT included in the public barrel.
 *
 * @internal
 */
export function __TESTING__BUILTIN_PATTERN_COUNT(): number {
  return BUILTIN_PATTERNS.length;
}
